'use client';

import { careTypeLabels } from '@selenos/vertical-spitex';
import {
  HeartPulseIcon,
  ShowerHeadIcon,
  HomeIcon,
  UsersIcon,
  HeartHandshakeIcon,
  HospitalIcon,
  BabyIcon,
  RefreshCwIcon,
  UserPlusIcon,
  ClockIcon,
} from 'lucide-react';

import { trackCareTypeSelected } from '~/lib/analytics/posthog-events';

interface CareTypeOption {
  key: string;
  icon: React.ComponentType<{
    className?: string;
    style?: React.CSSProperties;
  }>;
  description: string;
  kvg: boolean;
}

const careTypes: CareTypeOption[] = [
  {
    key: 'personal_care',
    icon: ShowerHeadIcon,
    description: 'Koerperpflege, Ankleiden, Mobilitaet',
    kvg: true,
  },
  {
    key: 'medical_care',
    icon: HeartPulseIcon,
    description: 'Wundversorgung, Injektionen, Medikamente',
    kvg: true,
  },
  {
    key: 'household_help',
    icon: HomeIcon,
    description: 'Reinigung, Kochen, Einkaufen',
    kvg: false,
  },
  {
    key: 'companionship',
    icon: UsersIcon,
    description: 'Soziale Begleitung, Ausfluege',
    kvg: false,
  },
  {
    key: 'palliative',
    icon: HeartHandshakeIcon,
    description: 'Pflege am Lebensende zu Hause',
    kvg: true,
  },
  {
    key: 'post_hospital',
    icon: HospitalIcon,
    description: 'Erholung und Pflege nach dem Spital',
    kvg: true,
  },
  {
    key: 'respite_care',
    icon: RefreshCwIcon,
    description: 'Entlastung fuer pflegende Angehoerige',
    kvg: false,
  },
  {
    key: 'child_care',
    icon: BabyIcon,
    description: 'Paediatrische Pflege zu Hause',
    kvg: true,
  },
  {
    key: 'angehoerigenpflege',
    icon: UserPlusIcon,
    description: 'Angehoerige als Pflegepersonal anstellen',
    kvg: false,
  },
  {
    key: 'acute_transitional',
    icon: ClockIcon,
    description: '2 Wochen nach Spitalaufenthalt, voll gedeckt',
    kvg: true,
  },
];

export function CareTypeSelector({ verticalSlug }: { verticalSlug: string }) {
  const handleSelect = (careTypeKey: string) => {
    trackCareTypeSelected(careTypeKey, verticalSlug);

    // Scroll to the form section and pass the care type
    const formSection = document.getElementById('lead-form');

    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });

      // Try to set the care type in a select element within the form
      const select = formSection.querySelector<HTMLSelectElement>(
        'select[name="careType"], select[data-field="careType"]',
      );

      if (select) {
        select.value = careTypeKey;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };

  return (
    <section className="bg-background w-full px-4 py-16 sm:py-20">
      <div className="mx-auto max-w-6xl">
        <p className="text-muted-foreground mb-2 text-center text-sm tracking-[0.2em] uppercase">
          Pflegearten
        </p>
        <h2 className="text-foreground mb-4 text-center text-2xl font-bold sm:text-3xl">
          Welche Pflege benoetigen Sie?
        </h2>
        <p className="text-muted-foreground mx-auto mb-12 max-w-xl text-center text-base">
          Waehlen Sie die gewuenschte Pflegeart. Wir finden qualifizierte
          Fachkraefte in Ihrem Kanton.
        </p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {careTypes.map((ct) => {
            const Icon = ct.icon;
            const label = careTypeLabels[ct.key] ?? ct.key;

            return (
              <button
                key={ct.key}
                type="button"
                onClick={() => handleSelect(ct.key)}
                className="bg-card group flex items-start gap-4 rounded-xl border p-5 text-left transition-all hover:border-teal-300 hover:shadow-md focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
              >
                <div
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg transition-colors group-hover:bg-teal-100"
                  style={{ backgroundColor: 'rgba(13, 148, 136, 0.08)' }}
                >
                  <Icon
                    className="h-6 w-6 transition-colors"
                    style={{ color: 'var(--landing-primary)' }}
                  />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-foreground text-base font-semibold">
                      {label}
                    </p>
                    {ct.kvg && (
                      <span className="inline-flex items-center rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-medium text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
                        KVG
                      </span>
                    )}
                  </div>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {ct.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        <p className="text-muted-foreground mt-6 text-center text-xs">
          KVG = von der Grundversicherung gedeckt (mit aerztlicher Verordnung)
        </p>
      </div>
    </section>
  );
}
